<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'AppDropdownMainMenu',
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    ...userComponentProps,
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const dashboards = ref(null);
  const ux = ref({
    loadingPlaceholder: 'none',
  });

  const $data = computed(() => ({
    dashboards: dashboards.value,
    ux: ux.value,
  }));

  const $$load_dashboards = () => {
    return new Promise((resolve) => {
      $fn.value
        .fetch('list_superset_dashboards')
        .then((p) => resolve(p.result))
        .catch((err) => {
          resolve({
            error: {
              message: _.get(
                err,
                'response.data.message',
                err.toJSON().message
              ),
            },
          });
        });
    });
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [{ name: 'dashboards' }, { name: 'ux' }],
    loaders: { dashboards: $$load_dashboards },
    ux: { loadingPlaceholder: 'none' },
    dataRefs: { dashboards, ux },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const sidebarCollapsed = computed(() => {
    return $app.value.sidebar ? $app.value.sidebar.collapsed : false;
  });
  const collapseIcon = computed(() => {
    return sidebarCollapsed.value ? 'mdi:dock-right' : 'mdi:dock-left';
  });

  const act_8564662037462133_script_xqa5x6fsl_value_callable = async (
    $event
  ) => {
    return $app.value.sidebar && $app.value.sidebar.toggle();
  };
  const act_8564662037462133_script_xqa5x6fsl_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_8564662037462133_script_xqa5x6fsl_value_callable($event);
      },
    };
  };
  const act_3312459938551810_showModal_2prr69yn5_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'CreateInvoice',
      props: {
        invoiceKind: 'outgoing',
      },
    };
  };
  const act_7711295589729650_showModal_xjd44dapf_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'CreateInvoice',
      props: {
        invoiceKind: 'incoming',
      },
    };
  };
  const act_6735074234099849_showModal_6b2lbil36_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'Payment',
      props: {
        transactionKind: 'cash_book',
        transactionType: 'deposit',
      },
    };
  };
  const act_1461016919552580_showModal_0kn0sfgvi_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'Payment',
      props: {
        transactionKind: 'cash_book',
        transactionType: 'payment',
      },
    };
  };
  const act_8159027920762481_showModal_bvpvxeecg_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'Payment',
      props: {
        transactionKind: 'bank',
      },
    };
  };
  const act_2045559189368907_showModal_yb4osqu82_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'AccountStatementReport',
    };
  };
  const act_2649299566695801_showModal_ooe1i83u6_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'MCreateFixedAsset',
      props: {
        assetType: 'general',
      },
    };
  };
  const act_7751968922212722_showModal_2ciqilfh9_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'MCreateFixedAsset',
      props: {
        assetType: 'waste_bin',
      },
    };
  };
  const act_5596318773635798_showModal_gwa26bq4k_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'CreateMeter',
    };
  };
  const act_8608073662829418_showModal_v6ctfvf78_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'ReplaceMeter',
    };
  };
  const act_3521576549885017_showModal_52b9uc7rp_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'MeterDataExchange',
    };
  };
  const act_8596272012843862_showModal_tg2zz4zox_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'CreateContract',
    };
  };
  const act_6524071025158589_showModal_fhmgn216f_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'BillingRules',
    };
  };
  const act_7495010493811270_showModal_cxts1ydi3_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'Worksheet',
      props: {
        size: 'lg',
      },
    };
  };
  const act_6865942025371558_showModal_w0xut4era_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'CreateProduct',
    };
  };
  const act_1823325530903709_showModal_eoo2i0has_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'Tariffs',
    };
  };
  const act_4588180971005511_showModal_vxahpmame_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'CreateRental',
    };
  };
  const act_1549954609779263_showModal_ni13jd0pu_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'CreateProject',
    };
  };
  const act_2781228788545099_showModal_7b49v4rsv_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'CreateProperty',
    };
  };
  const act_4583127870703770_showModal_p7zjgq8mw_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'CreateCompany',
      props: {
        role: 'partner',
      },
    };
  };
  const act_7658371602827426_showModal_4nhlterke_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'CreateCompany',
      props: {
        role: 'own',
      },
    };
  };

  const act_7315092382398562_reloadComponentData_bvb792cne =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_zcdvexidp =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_bvb792cne,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_zcdvexidp],
    event: 'reload',
  });
  const act_8564662037462133_script_xqa5x6fsl = new actionTypes.ScriptAction({
    actionArgs: act_8564662037462133_script_xqa5x6fsl_getActionArgs,
  });
  const inst_evh_8564662037462133_click_drg3yytku =
    new actionTypes.EventHandlerImpl({
      action: act_8564662037462133_script_xqa5x6fsl,
      event: 'click',
      displayName: 'script',
    });
  const inst_evh_8564662037462133_click = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_8564662037462133_click_drg3yytku],
    event: 'click',
  });
  const act_3312459938551810_showModal_2prr69yn5 =
    new actionTypes.ShowModalAction({
      actionArgs: act_3312459938551810_showModal_2prr69yn5_getActionArgs,
    });
  const inst_evh_3312459938551810_clickPrevent_ovwa7g5ua =
    new actionTypes.EventHandlerImpl({
      action: act_3312459938551810_showModal_2prr69yn5,
      event: 'click.prevent',
      displayName: 'showModal',
    });
  const inst_evh_3312459938551810_clickPrevent =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_3312459938551810_clickPrevent_ovwa7g5ua],
      event: 'click.prevent',
    });
  const act_7711295589729650_showModal_xjd44dapf =
    new actionTypes.ShowModalAction({
      actionArgs: act_7711295589729650_showModal_xjd44dapf_getActionArgs,
    });
  const inst_evh_7711295589729650_clickPrevent_ky15u0dkt =
    new actionTypes.EventHandlerImpl({
      action: act_7711295589729650_showModal_xjd44dapf,
      event: 'click.prevent',
      displayName: 'showModal',
    });
  const inst_evh_7711295589729650_clickPrevent =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_7711295589729650_clickPrevent_ky15u0dkt],
      event: 'click.prevent',
    });
  const act_6735074234099849_showModal_6b2lbil36 =
    new actionTypes.ShowModalAction({
      actionArgs: act_6735074234099849_showModal_6b2lbil36_getActionArgs,
    });
  const inst_evh_6735074234099849_clickPrevent_r1qeoh89h =
    new actionTypes.EventHandlerImpl({
      action: act_6735074234099849_showModal_6b2lbil36,
      event: 'click.prevent',
      displayName: 'showModal',
    });
  const inst_evh_6735074234099849_clickPrevent =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_6735074234099849_clickPrevent_r1qeoh89h],
      event: 'click.prevent',
    });
  const act_1461016919552580_showModal_0kn0sfgvi =
    new actionTypes.ShowModalAction({
      actionArgs: act_1461016919552580_showModal_0kn0sfgvi_getActionArgs,
    });
  const inst_evh_1461016919552580_clickPrevent_75gepckjr =
    new actionTypes.EventHandlerImpl({
      action: act_1461016919552580_showModal_0kn0sfgvi,
      event: 'click.prevent',
      displayName: 'showModal',
    });
  const inst_evh_1461016919552580_clickPrevent =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_1461016919552580_clickPrevent_75gepckjr],
      event: 'click.prevent',
    });
  const act_8159027920762481_showModal_bvpvxeecg =
    new actionTypes.ShowModalAction({
      actionArgs: act_8159027920762481_showModal_bvpvxeecg_getActionArgs,
    });
  const inst_evh_8159027920762481_clickPrevent_s2xqpcv0o =
    new actionTypes.EventHandlerImpl({
      action: act_8159027920762481_showModal_bvpvxeecg,
      event: 'click.prevent',
      displayName: 'showModal',
    });
  const inst_evh_8159027920762481_clickPrevent =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_8159027920762481_clickPrevent_s2xqpcv0o],
      event: 'click.prevent',
    });
  const act_2045559189368907_showModal_yb4osqu82 =
    new actionTypes.ShowModalAction({
      actionArgs: act_2045559189368907_showModal_yb4osqu82_getActionArgs,
    });
  const inst_evh_2045559189368907_clickPrevent_h3tt5msyy =
    new actionTypes.EventHandlerImpl({
      action: act_2045559189368907_showModal_yb4osqu82,
      event: 'click.prevent',
      displayName: 'showModal',
    });
  const inst_evh_2045559189368907_clickPrevent =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2045559189368907_clickPrevent_h3tt5msyy],
      event: 'click.prevent',
    });
  const act_2649299566695801_showModal_ooe1i83u6 =
    new actionTypes.ShowModalAction({
      actionArgs: act_2649299566695801_showModal_ooe1i83u6_getActionArgs,
    });
  const inst_evh_2649299566695801_clickPrevent_s3c3cxchx =
    new actionTypes.EventHandlerImpl({
      action: act_2649299566695801_showModal_ooe1i83u6,
      event: 'click.prevent',
      displayName: 'showModal',
    });
  const inst_evh_2649299566695801_clickPrevent =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2649299566695801_clickPrevent_s3c3cxchx],
      event: 'click.prevent',
    });
  const act_7751968922212722_showModal_2ciqilfh9 =
    new actionTypes.ShowModalAction({
      actionArgs: act_7751968922212722_showModal_2ciqilfh9_getActionArgs,
    });
  const inst_evh_7751968922212722_clickPrevent_eacfn60uh =
    new actionTypes.EventHandlerImpl({
      action: act_7751968922212722_showModal_2ciqilfh9,
      event: 'click.prevent',
      displayName: 'showModal',
    });
  const inst_evh_7751968922212722_clickPrevent =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_7751968922212722_clickPrevent_eacfn60uh],
      event: 'click.prevent',
    });
  const act_5596318773635798_showModal_gwa26bq4k =
    new actionTypes.ShowModalAction({
      actionArgs: act_5596318773635798_showModal_gwa26bq4k_getActionArgs,
    });
  const inst_evh_5596318773635798_clickPrevent_9v065s1u5 =
    new actionTypes.EventHandlerImpl({
      action: act_5596318773635798_showModal_gwa26bq4k,
      event: 'click.prevent',
      displayName: 'showModal',
    });
  const inst_evh_5596318773635798_clickPrevent =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_5596318773635798_clickPrevent_9v065s1u5],
      event: 'click.prevent',
    });
  const act_8608073662829418_showModal_v6ctfvf78 =
    new actionTypes.ShowModalAction({
      actionArgs: act_8608073662829418_showModal_v6ctfvf78_getActionArgs,
    });
  const inst_evh_8608073662829418_clickPrevent_x5jvez7a4 =
    new actionTypes.EventHandlerImpl({
      action: act_8608073662829418_showModal_v6ctfvf78,
      event: 'click.prevent',
      displayName: 'showModal',
    });
  const inst_evh_8608073662829418_clickPrevent =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_8608073662829418_clickPrevent_x5jvez7a4],
      event: 'click.prevent',
    });
  const act_3521576549885017_showModal_52b9uc7rp =
    new actionTypes.ShowModalAction({
      actionArgs: act_3521576549885017_showModal_52b9uc7rp_getActionArgs,
    });
  const inst_evh_3521576549885017_clickPrevent_819rg4fth =
    new actionTypes.EventHandlerImpl({
      action: act_3521576549885017_showModal_52b9uc7rp,
      event: 'click.prevent',
      displayName: 'showModal',
    });
  const inst_evh_3521576549885017_clickPrevent =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_3521576549885017_clickPrevent_819rg4fth],
      event: 'click.prevent',
    });
  const act_8596272012843862_showModal_tg2zz4zox =
    new actionTypes.ShowModalAction({
      actionArgs: act_8596272012843862_showModal_tg2zz4zox_getActionArgs,
    });
  const inst_evh_8596272012843862_clickPrevent_wrmdodnz7 =
    new actionTypes.EventHandlerImpl({
      action: act_8596272012843862_showModal_tg2zz4zox,
      event: 'click.prevent',
      displayName: 'showModal',
    });
  const inst_evh_8596272012843862_clickPrevent =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_8596272012843862_clickPrevent_wrmdodnz7],
      event: 'click.prevent',
    });
  const act_6524071025158589_showModal_fhmgn216f =
    new actionTypes.ShowModalAction({
      actionArgs: act_6524071025158589_showModal_fhmgn216f_getActionArgs,
    });
  const inst_evh_6524071025158589_clickPrevent_0qjv9cfom =
    new actionTypes.EventHandlerImpl({
      action: act_6524071025158589_showModal_fhmgn216f,
      event: 'click.prevent',
      displayName: 'showModal',
    });
  const inst_evh_6524071025158589_clickPrevent =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_6524071025158589_clickPrevent_0qjv9cfom],
      event: 'click.prevent',
    });
  const act_7495010493811270_showModal_cxts1ydi3 =
    new actionTypes.ShowModalAction({
      actionArgs: act_7495010493811270_showModal_cxts1ydi3_getActionArgs,
    });
  const inst_evh_7495010493811270_clickPrevent_j08zddfhh =
    new actionTypes.EventHandlerImpl({
      action: act_7495010493811270_showModal_cxts1ydi3,
      event: 'click.prevent',
      displayName: 'showModal',
    });
  const inst_evh_7495010493811270_clickPrevent =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_7495010493811270_clickPrevent_j08zddfhh],
      event: 'click.prevent',
    });
  const act_6865942025371558_showModal_w0xut4era =
    new actionTypes.ShowModalAction({
      actionArgs: act_6865942025371558_showModal_w0xut4era_getActionArgs,
    });
  const inst_evh_6865942025371558_clickPrevent_4sk5twj3z =
    new actionTypes.EventHandlerImpl({
      action: act_6865942025371558_showModal_w0xut4era,
      event: 'click.prevent',
      displayName: 'showModal',
    });
  const inst_evh_6865942025371558_clickPrevent =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_6865942025371558_clickPrevent_4sk5twj3z],
      event: 'click.prevent',
    });
  const act_1823325530903709_showModal_eoo2i0has =
    new actionTypes.ShowModalAction({
      actionArgs: act_1823325530903709_showModal_eoo2i0has_getActionArgs,
    });
  const inst_evh_1823325530903709_clickPrevent_6avxms3dc =
    new actionTypes.EventHandlerImpl({
      action: act_1823325530903709_showModal_eoo2i0has,
      event: 'click.prevent',
      displayName: 'showModal',
    });
  const inst_evh_1823325530903709_clickPrevent =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_1823325530903709_clickPrevent_6avxms3dc],
      event: 'click.prevent',
    });
  const act_4588180971005511_showModal_vxahpmame =
    new actionTypes.ShowModalAction({
      actionArgs: act_4588180971005511_showModal_vxahpmame_getActionArgs,
    });
  const inst_evh_4588180971005511_clickPrevent_oriuickua =
    new actionTypes.EventHandlerImpl({
      action: act_4588180971005511_showModal_vxahpmame,
      event: 'click.prevent',
      displayName: 'showModal',
    });
  const inst_evh_4588180971005511_clickPrevent =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_4588180971005511_clickPrevent_oriuickua],
      event: 'click.prevent',
    });
  const act_1549954609779263_showModal_ni13jd0pu =
    new actionTypes.ShowModalAction({
      actionArgs: act_1549954609779263_showModal_ni13jd0pu_getActionArgs,
    });
  const inst_evh_1549954609779263_clickPrevent_4cuaiqicp =
    new actionTypes.EventHandlerImpl({
      action: act_1549954609779263_showModal_ni13jd0pu,
      event: 'click.prevent',
      displayName: 'showModal',
    });
  const inst_evh_1549954609779263_clickPrevent =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_1549954609779263_clickPrevent_4cuaiqicp],
      event: 'click.prevent',
    });
  const act_2781228788545099_showModal_7b49v4rsv =
    new actionTypes.ShowModalAction({
      actionArgs: act_2781228788545099_showModal_7b49v4rsv_getActionArgs,
    });
  const inst_evh_2781228788545099_clickPrevent_uzuiquhi8 =
    new actionTypes.EventHandlerImpl({
      action: act_2781228788545099_showModal_7b49v4rsv,
      event: 'click.prevent',
      displayName: 'showModal',
    });
  const inst_evh_2781228788545099_clickPrevent =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2781228788545099_clickPrevent_uzuiquhi8],
      event: 'click.prevent',
    });
  const act_4583127870703770_showModal_p7zjgq8mw =
    new actionTypes.ShowModalAction({
      actionArgs: act_4583127870703770_showModal_p7zjgq8mw_getActionArgs,
    });
  const inst_evh_4583127870703770_clickPrevent_o5womemp6 =
    new actionTypes.EventHandlerImpl({
      action: act_4583127870703770_showModal_p7zjgq8mw,
      event: 'click.prevent',
      displayName: 'showModal',
    });
  const inst_evh_4583127870703770_clickPrevent =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_4583127870703770_clickPrevent_o5womemp6],
      event: 'click.prevent',
    });
  const act_7658371602827426_showModal_4nhlterke =
    new actionTypes.ShowModalAction({
      actionArgs: act_7658371602827426_showModal_4nhlterke_getActionArgs,
    });
  const inst_evh_7658371602827426_clickPrevent_ebiq1qlh1 =
    new actionTypes.EventHandlerImpl({
      action: act_7658371602827426_showModal_4nhlterke,
      event: 'click.prevent',
      displayName: 'showModal',
    });
  const inst_evh_7658371602827426_clickPrevent =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_7658371602827426_clickPrevent_ebiq1qlh1],
      event: 'click.prevent',
    });

  const actions = {
    inst_evh_7315092382398562_reload_zcdvexidp,
    inst_evh_7315092382398562_reload,
    inst_evh_8564662037462133_click_drg3yytku,
    inst_evh_8564662037462133_click,
    inst_evh_3312459938551810_clickPrevent_ovwa7g5ua,
    inst_evh_3312459938551810_clickPrevent,
    inst_evh_7711295589729650_clickPrevent_ky15u0dkt,
    inst_evh_7711295589729650_clickPrevent,
    inst_evh_6735074234099849_clickPrevent_r1qeoh89h,
    inst_evh_6735074234099849_clickPrevent,
    inst_evh_1461016919552580_clickPrevent_75gepckjr,
    inst_evh_1461016919552580_clickPrevent,
    inst_evh_8159027920762481_clickPrevent_s2xqpcv0o,
    inst_evh_8159027920762481_clickPrevent,
    inst_evh_2045559189368907_clickPrevent_h3tt5msyy,
    inst_evh_2045559189368907_clickPrevent,
    inst_evh_2649299566695801_clickPrevent_s3c3cxchx,
    inst_evh_2649299566695801_clickPrevent,
    inst_evh_7751968922212722_clickPrevent_eacfn60uh,
    inst_evh_7751968922212722_clickPrevent,
    inst_evh_5596318773635798_clickPrevent_9v065s1u5,
    inst_evh_5596318773635798_clickPrevent,
    inst_evh_8608073662829418_clickPrevent_x5jvez7a4,
    inst_evh_8608073662829418_clickPrevent,
    inst_evh_3521576549885017_clickPrevent_819rg4fth,
    inst_evh_3521576549885017_clickPrevent,
    inst_evh_8596272012843862_clickPrevent_wrmdodnz7,
    inst_evh_8596272012843862_clickPrevent,
    inst_evh_6524071025158589_clickPrevent_0qjv9cfom,
    inst_evh_6524071025158589_clickPrevent,
    inst_evh_7495010493811270_clickPrevent_j08zddfhh,
    inst_evh_7495010493811270_clickPrevent,
    inst_evh_6865942025371558_clickPrevent_4sk5twj3z,
    inst_evh_6865942025371558_clickPrevent,
    inst_evh_1823325530903709_clickPrevent_6avxms3dc,
    inst_evh_1823325530903709_clickPrevent,
    inst_evh_4588180971005511_clickPrevent_oriuickua,
    inst_evh_4588180971005511_clickPrevent,
    inst_evh_1549954609779263_clickPrevent_4cuaiqicp,
    inst_evh_1549954609779263_clickPrevent,
    inst_evh_2781228788545099_clickPrevent_uzuiquhi8,
    inst_evh_2781228788545099_clickPrevent,
    inst_evh_4583127870703770_clickPrevent_o5womemp6,
    inst_evh_4583127870703770_clickPrevent,
    inst_evh_7658371602827426_clickPrevent_ebiq1qlh1,
    inst_evh_7658371602827426_clickPrevent,
  };

  const evh_7315092382398562_reload_zcdvexidp = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_zcdvexidp.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_click_drg3yytku = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_click_drg3yytku.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_click = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_click.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3312459938551810_clickPrevent_ovwa7g5ua = async (event, scope) => {
    return await actions.inst_evh_3312459938551810_clickPrevent_ovwa7g5ua.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3312459938551810_clickPrevent = async (event, scope) => {
    return await actions.inst_evh_3312459938551810_clickPrevent.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7711295589729650_clickPrevent_ky15u0dkt = async (event, scope) => {
    return await actions.inst_evh_7711295589729650_clickPrevent_ky15u0dkt.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7711295589729650_clickPrevent = async (event, scope) => {
    return await actions.inst_evh_7711295589729650_clickPrevent.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6735074234099849_clickPrevent_r1qeoh89h = async (event, scope) => {
    return await actions.inst_evh_6735074234099849_clickPrevent_r1qeoh89h.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6735074234099849_clickPrevent = async (event, scope) => {
    return await actions.inst_evh_6735074234099849_clickPrevent.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1461016919552580_clickPrevent_75gepckjr = async (event, scope) => {
    return await actions.inst_evh_1461016919552580_clickPrevent_75gepckjr.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1461016919552580_clickPrevent = async (event, scope) => {
    return await actions.inst_evh_1461016919552580_clickPrevent.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8159027920762481_clickPrevent_s2xqpcv0o = async (event, scope) => {
    return await actions.inst_evh_8159027920762481_clickPrevent_s2xqpcv0o.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8159027920762481_clickPrevent = async (event, scope) => {
    return await actions.inst_evh_8159027920762481_clickPrevent.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2045559189368907_clickPrevent_h3tt5msyy = async (event, scope) => {
    return await actions.inst_evh_2045559189368907_clickPrevent_h3tt5msyy.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2045559189368907_clickPrevent = async (event, scope) => {
    return await actions.inst_evh_2045559189368907_clickPrevent.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2649299566695801_clickPrevent_s3c3cxchx = async (event, scope) => {
    return await actions.inst_evh_2649299566695801_clickPrevent_s3c3cxchx.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2649299566695801_clickPrevent = async (event, scope) => {
    return await actions.inst_evh_2649299566695801_clickPrevent.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7751968922212722_clickPrevent_eacfn60uh = async (event, scope) => {
    return await actions.inst_evh_7751968922212722_clickPrevent_eacfn60uh.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7751968922212722_clickPrevent = async (event, scope) => {
    return await actions.inst_evh_7751968922212722_clickPrevent.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5596318773635798_clickPrevent_9v065s1u5 = async (event, scope) => {
    return await actions.inst_evh_5596318773635798_clickPrevent_9v065s1u5.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5596318773635798_clickPrevent = async (event, scope) => {
    return await actions.inst_evh_5596318773635798_clickPrevent.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8608073662829418_clickPrevent_x5jvez7a4 = async (event, scope) => {
    return await actions.inst_evh_8608073662829418_clickPrevent_x5jvez7a4.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8608073662829418_clickPrevent = async (event, scope) => {
    return await actions.inst_evh_8608073662829418_clickPrevent.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3521576549885017_clickPrevent_819rg4fth = async (event, scope) => {
    return await actions.inst_evh_3521576549885017_clickPrevent_819rg4fth.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3521576549885017_clickPrevent = async (event, scope) => {
    return await actions.inst_evh_3521576549885017_clickPrevent.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8596272012843862_clickPrevent_wrmdodnz7 = async (event, scope) => {
    return await actions.inst_evh_8596272012843862_clickPrevent_wrmdodnz7.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8596272012843862_clickPrevent = async (event, scope) => {
    return await actions.inst_evh_8596272012843862_clickPrevent.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6524071025158589_clickPrevent_0qjv9cfom = async (event, scope) => {
    return await actions.inst_evh_6524071025158589_clickPrevent_0qjv9cfom.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6524071025158589_clickPrevent = async (event, scope) => {
    return await actions.inst_evh_6524071025158589_clickPrevent.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7495010493811270_clickPrevent_j08zddfhh = async (event, scope) => {
    return await actions.inst_evh_7495010493811270_clickPrevent_j08zddfhh.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7495010493811270_clickPrevent = async (event, scope) => {
    return await actions.inst_evh_7495010493811270_clickPrevent.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6865942025371558_clickPrevent_4sk5twj3z = async (event, scope) => {
    return await actions.inst_evh_6865942025371558_clickPrevent_4sk5twj3z.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6865942025371558_clickPrevent = async (event, scope) => {
    return await actions.inst_evh_6865942025371558_clickPrevent.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1823325530903709_clickPrevent_6avxms3dc = async (event, scope) => {
    return await actions.inst_evh_1823325530903709_clickPrevent_6avxms3dc.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1823325530903709_clickPrevent = async (event, scope) => {
    return await actions.inst_evh_1823325530903709_clickPrevent.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4588180971005511_clickPrevent_oriuickua = async (event, scope) => {
    return await actions.inst_evh_4588180971005511_clickPrevent_oriuickua.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4588180971005511_clickPrevent = async (event, scope) => {
    return await actions.inst_evh_4588180971005511_clickPrevent.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1549954609779263_clickPrevent_4cuaiqicp = async (event, scope) => {
    return await actions.inst_evh_1549954609779263_clickPrevent_4cuaiqicp.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1549954609779263_clickPrevent = async (event, scope) => {
    return await actions.inst_evh_1549954609779263_clickPrevent.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2781228788545099_clickPrevent_uzuiquhi8 = async (event, scope) => {
    return await actions.inst_evh_2781228788545099_clickPrevent_uzuiquhi8.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2781228788545099_clickPrevent = async (event, scope) => {
    return await actions.inst_evh_2781228788545099_clickPrevent.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4583127870703770_clickPrevent_o5womemp6 = async (event, scope) => {
    return await actions.inst_evh_4583127870703770_clickPrevent_o5womemp6.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4583127870703770_clickPrevent = async (event, scope) => {
    return await actions.inst_evh_4583127870703770_clickPrevent.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7658371602827426_clickPrevent_ebiq1qlh1 = async (event, scope) => {
    return await actions.inst_evh_7658371602827426_clickPrevent_ebiq1qlh1.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7658371602827426_clickPrevent = async (event, scope) => {
    return await actions.inst_evh_7658371602827426_clickPrevent.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpUsercontrol
    :loading="loading"
    :reloading="reloading"
    :loaded="loaded"
    :error="error"
    loadingPlaceholder="none"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <BNavbarNav class="main-menu-sidebar">
      <button
        type="button"
        :title="pgettext('SidebarHeader', 'Toggle sidebar')"
        class="main-menu-sidebar__collapse-btn"
        @click="evh_8564662037462133_click($event, {})"
      >
        <ItpIcon :name="collapseIcon"> </ItpIcon>
      </button>
      <ItpAppsDropdown
        icon="mdi:dots-grid"
        :title="pgettext('MainMenu', 'Apps')"
      >
        <ItpDropdownSubmenu :label="pgettext('MainMenu', 'Finance')">
          <ItpDropdownText>
            {{ pgettext('MainMenu', 'Register') }}
          </ItpDropdownText>
          <ItpNavItem
            to="/registers"
            :icon="$config.register.icon"
            :text="pgettext('MainMenu', 'Registers')"
            dropdown-item
          >
          </ItpNavItem>
          <ItpDropdownDivider> </ItpDropdownDivider>
          <ItpDropdownText>
            {{ pgettext('MainMenu', 'Customer invoices') }}
          </ItpDropdownText>
          <ItpNavItem
            to="/invoices-out"
            :icon="$config.invoice.customerInvoiceIcon"
            :text="pgettext('MainMenu', 'Customer invoices')"
            dropdown-item
          >
          </ItpNavItem>
          <ItpNavItem
            :text="pgettext('MainMenu', 'New Customer Invoice...')"
            :icon="$config.invoice.customerInvoiceIcon"
            dropdown-item
            @click.prevent="evh_3312459938551810_clickPrevent($event, {})"
          >
          </ItpNavItem>
          <ItpNavItem
            to="/billing-list"
            :icon="$config.billingList.icon"
            :text="pgettext('MainMenu', 'Billing List')"
            dropdown-item
          >
          </ItpNavItem>
          <ItpNavItem
            to="/invoice-generation"
            :icon="$config.invoiceGeneration.icon"
            :text="pgettext('MainMenu', 'Invoice Generation')"
            dropdown-item
          >
          </ItpNavItem>
          <ItpDropdownDivider> </ItpDropdownDivider>
          <ItpDropdownText>
            {{ pgettext('MainMenu', 'Incoming invoices') }}
          </ItpDropdownText>
          <ItpNavItem
            to="/invoices-in"
            :icon="$config.invoice.vendorInvoiceIcon"
            :text="pgettext('MainMenu', 'Incoming invoices')"
            dropdown-item
          >
          </ItpNavItem>
          <ItpNavItem
            :text="pgettext('MainMenu', 'New Incoming Invoice...')"
            :icon="$config.invoice.icon"
            dropdown-item
            @click.prevent="evh_7711295589729650_clickPrevent($event, {})"
          >
          </ItpNavItem>
          <ItpDropdownDivider> </ItpDropdownDivider>
          <ItpDropdownText>
            {{ pgettext('MainMenu', 'Payments') }}
          </ItpDropdownText>
          <ItpNavItem
            to="/payments"
            :icon="$config.payment.paymentsList.icon"
            :text="pgettext('PaymentsView', 'Payments')"
            dropdown-item
          >
          </ItpNavItem>
          <ItpNavItem
            to="/accounts-daily-flow"
            :icon="$config.accountsDailyFlow.icon"
            :text="pgettext('MainMenu', 'Accounts Daily Flow')"
            dropdown-item
          >
          </ItpNavItem>
          <ItpNavItem
            :text="pgettext('Create Payment', 'New Cash In...')"
            :icon="$config.payment.cash.icon"
            dropdown-item
            @click.prevent="evh_6735074234099849_clickPrevent($event, {})"
          >
          </ItpNavItem>
          <ItpNavItem
            :text="pgettext('Create Payment', 'New Cash Out...')"
            :icon="$config.payment.cash.icon"
            dropdown-item
            @click.prevent="evh_1461016919552580_clickPrevent($event, {})"
          >
          </ItpNavItem>
          <ItpNavItem
            :text="pgettext('Create Payment', 'New Banking...')"
            :icon="$config.payment.banking.icon"
            dropdown-item
            @click.prevent="evh_8159027920762481_clickPrevent($event, {})"
          >
          </ItpNavItem>
          <ItpNavItem
            :text="pgettext('MeinMenu', 'Account statement report...')"
            :icon="$config.accountStatementReport.icon"
            dropdown-item
            @click.prevent="evh_2045559189368907_clickPrevent($event, {})"
          >
          </ItpNavItem>
          <ItpDropdownDivider> </ItpDropdownDivider>
          <ItpDropdownText>
            {{ pgettext('MainMenu', 'Claims management') }}
          </ItpDropdownText>
          <ItpNavItem
            to="/claims"
            :icon="$config.claim.icon"
            :text="pgettext('MainMenu', 'Claims')"
            dropdown-item
          >
          </ItpNavItem>
          <ItpNavItem
            to="/dunning-letters"
            :icon="$config.dunningLetter.icon"
            :text="pgettext('MainMenu', 'Dunning letters')"
            dropdown-item
          >
          </ItpNavItem>
        </ItpDropdownSubmenu>
        <ItpDropdownSubmenu :label="pgettext('MainMenu', 'Assets')">
          <ItpNavItem
            to="/fixed-assets"
            :icon="$config.fixedAsset.icon"
            :text="pgettext('MainMenu', 'Fixed Assets')"
            dropdown-item
          >
          </ItpNavItem>
          <ItpNavItem
            :text="pgettext('MainMenu', 'Create Asset...')"
            :icon="$config.fixedAsset.icon"
            dropdown-item
            @click.prevent="evh_2649299566695801_clickPrevent($event, {})"
          >
          </ItpNavItem>
          <ItpDropdownDivider> </ItpDropdownDivider>
          <ItpNavItem
            to="/waste-bins"
            :icon="$config.wasteBin.icon"
            :text="pgettext('MainMenu', 'Waste Bins')"
            dropdown-item
          >
          </ItpNavItem>
          <ItpNavItem
            to="/waste-bin-readings"
            :icon="$config.wasteBin.icon"
            :text="pgettext('MainMenu', 'Waste Bin Readings')"
            dropdown-item
          >
          </ItpNavItem>
          <ItpNavItem
            :text="pgettext('MainMenu', 'Create Waste Bin...')"
            :icon="$config.wasteBin.createWasteBinIcon"
            dropdown-item
            @click.prevent="evh_7751968922212722_clickPrevent($event, {})"
          >
          </ItpNavItem>
          <ItpDropdownDivider> </ItpDropdownDivider>
          <ItpDropdownText>
            {{ pgettext('MainMenu', 'Meters') }}
          </ItpDropdownText>
          <ItpNavItem
            to="/meters"
            :icon="$config.meter.icon"
            :text="pgettext('MainMenu', 'Meters')"
            dropdown-item
          >
          </ItpNavItem>
          <ItpNavItem
            :text="pgettext('MainMenu', 'Add Meter...')"
            :icon="$config.meter.addMeterIcon"
            dropdown-item
            @click.prevent="evh_5596318773635798_clickPrevent($event, {})"
          >
          </ItpNavItem>
          <ItpNavItem
            href="#"
            :icon="$config.meterReplacement.icon"
            :text="pgettext('MainMenu', 'Replace meter...')"
            dropdown-item
            @click.prevent="evh_8608073662829418_clickPrevent($event, {})"
          >
          </ItpNavItem>
          <ItpNavItem
            to="/auto-readings"
            :icon="$config.meterAutoReading.icon"
            :text="pgettext('MainMenu', 'Auto Readings')"
            dropdown-item
          >
          </ItpNavItem>
          <ItpNavItem
            href="#"
            icon="fa-upload"
            :text="pgettext('MainMenu', 'Meter Data Exchange...')"
            dropdown-item
            @click.prevent="evh_3521576549885017_clickPrevent($event, {})"
          >
          </ItpNavItem>
          <ItpNavItem
            to="/meter-consumptions"
            :icon="$config.meterConsumption.icon"
            :text="pgettext('MainMenu', 'Meter consumptions')"
            dropdown-item
          >
          </ItpNavItem>
        </ItpDropdownSubmenu>
        <ItpDropdownSubmenu :label="pgettext('MainMenu', 'Contracts')">
          <ItpNavItem
            to="/contracts"
            :icon="$config.contract.icon"
            :text="pgettext('MainMenu', 'Contracts')"
            dropdown-item
          >
          </ItpNavItem>
          <ItpNavItem
            :text="pgettext('MainMenu', 'Create Contract...')"
            :icon="$config.contract.icon"
            dropdown-item
            @click.prevent="evh_8596272012843862_clickPrevent($event, {})"
          >
          </ItpNavItem>
          <ItpNavItem
            to="/contract-services"
            :icon="$config.contract.icon"
            :text="pgettext('MainMenu', 'Contract services')"
            dropdown-item
          >
          </ItpNavItem>
          <ItpNavItem
            to="/contract-service-fees"
            :icon="$config.contract.icon"
            :text="pgettext('MainMenu', 'Contract service fees')"
            dropdown-item
          >
          </ItpNavItem>
          <ItpNavItem
            to="/contract-attachments"
            :icon="$config.attachmentIcon"
            :text="pgettext('MainMenu', 'Contract attachments')"
            dropdown-item
          >
          </ItpNavItem>
          <ItpNavItem
            to="/contract-events"
            :icon="$config.contract.views.contractEvents.icon"
            :text="pgettext('MainMenu', 'Contract events')"
            dropdown-item
          >
          </ItpNavItem>
          <ItpDropdownDivider> </ItpDropdownDivider>
          <ItpNavItem
            to="/overlapping-revisions"
            :icon="$config.contract.views.overlappingRevisions.icon"
            :text="pgettext('MainMenu', 'Overlapping Revisions')"
            dropdown-item
          >
          </ItpNavItem>
          <ItpNavItem
            href="#"
            icon="gravity-ui:curly-brackets-function"
            :text="pgettext('MainMenu', 'Billing rules')"
            dropdown-item
            @click.prevent="evh_6524071025158589_clickPrevent($event, {})"
          >
          </ItpNavItem>
        </ItpDropdownSubmenu>
        <ItpDropdownSubmenu :label="pgettext('MainMenu', 'Worksheets')">
          <ItpNavItem
            to="/worksheets"
            :icon="$config.worksheet.icon"
            :text="pgettext('MainMenu', 'Worksheets')"
            dropdown-item
          >
          </ItpNavItem>
          <ItpNavItem
            :text="pgettext('MainMenu', 'New Worksheet...')"
            :icon="$config.worksheet.createWorksheetIcon"
            dropdown-item
            @click.prevent="evh_7495010493811270_clickPrevent($event, {})"
          >
          </ItpNavItem>
          <ItpNavItem
            to="/worksheets-work-summary"
            :icon="$config.worksheet.icon"
            :text="pgettext('MainMenu', 'Work summary')"
            dropdown-item
          >
          </ItpNavItem>
        </ItpDropdownSubmenu>
        <ItpDropdownSubmenu :label="pgettext('MainMenu', 'Master data')">
          <ItpNavItem
            to="/products"
            :icon="$config.product.icon"
            :text="pgettext('MainMenu', 'Products')"
            dropdown-item
          >
          </ItpNavItem>
          <ItpNavItem
            :text="pgettext('MainMenu', 'New product/service...')"
            :icon="$config.product.addProductIcon"
            dropdown-item
            @click.prevent="evh_6865942025371558_clickPrevent($event, {})"
          >
          </ItpNavItem>
          <ItpNavItem
            :text="pgettext('MainMenu', 'Tariffs...')"
            :icon="$config.tariff.icon"
            dropdown-item
            @click.prevent="evh_1823325530903709_clickPrevent($event, {})"
          >
          </ItpNavItem>
          <ItpDropdownDivider> </ItpDropdownDivider>
          <ItpNavItem
            to="/rentals"
            :icon="$config.rental.icon"
            :text="pgettext('MainMenu', 'Rentals')"
            dropdown-item
          >
          </ItpNavItem>
          <ItpNavItem
            :text="pgettext('MainMenu', 'Add Rental...')"
            :icon="$config.rental.addRentalIcon"
            dropdown-item
            @click.prevent="evh_4588180971005511_clickPrevent($event, {})"
          >
          </ItpNavItem>
          <ItpNavItem
            to="/rental-services"
            :icon="$config.rental.icon"
            :text="pgettext('MainMenu', 'Rental services')"
            dropdown-item
          >
          </ItpNavItem>
          <ItpDropdownDivider> </ItpDropdownDivider>
          <ItpNavItem
            to="/projects"
            :icon="$config.project.icon"
            :text="pgettext('MainMenu', 'Projects')"
            dropdown-item
          >
          </ItpNavItem>
          <ItpNavItem
            :text="pgettext('MainMenu', 'Add project...')"
            :icon="$config.project.addProjectIcon"
            dropdown-item
            @click.prevent="evh_1549954609779263_clickPrevent($event, {})"
          >
          </ItpNavItem>
          <ItpDropdownDivider> </ItpDropdownDivider>
          <ItpNavItem
            to="/properties"
            :icon="$config.property.icon"
            :text="pgettext('MainMenu', 'Locations')"
            dropdown-item
          >
          </ItpNavItem>
          <ItpNavItem
            :text="pgettext('MainMenu', 'Add property...')"
            :icon="$config.property.addPropertyIcon"
            dropdown-item
            @click.prevent="evh_2781228788545099_clickPrevent($event, {})"
          >
          </ItpNavItem>
          <ItpDropdownDivider> </ItpDropdownDivider>
          <ItpNavItem
            to="/partners"
            :icon="$config.company.icons.partner"
            :text="pgettext('MainMenu', 'Partners')"
            dropdown-item
          >
          </ItpNavItem>
          <ItpNavItem
            :text="pgettext('MainMenu', 'Add partner...')"
            :icon="$config.company.icons.partner"
            dropdown-item
            @click.prevent="evh_4583127870703770_clickPrevent($event, {})"
          >
          </ItpNavItem>
          <ItpDropdownDivider> </ItpDropdownDivider>
          <ItpNavItem
            to="/own-companies"
            :icon="$config.company.icons.own"
            :text="pgettext('MainMenu', 'Own companies')"
            dropdown-item
          >
          </ItpNavItem>
          <ItpNavItem
            :text="pgettext('MainMenu', 'Add company...')"
            :icon="$config.company.icons.own"
            dropdown-item
            @click.prevent="evh_7658371602827426_clickPrevent($event, {})"
          >
          </ItpNavItem>
        </ItpDropdownSubmenu>
        <ItpDropdownSubmenu :label="pgettext('MainMenu', 'Reports')">
          <ItpNavItem
            v-if="dashboards && dashboards.error && dashboards.error.message"
          >
            <ItpText
              wrap
              :title="dashboards.error.message"
              :text="dashboards.error.message"
            >
            </ItpText>
          </ItpNavItem>
          <template v-for="item in dashboards">
            <ItpNavItem
              :key="item.id"
              :to="'/charts/' + item.id"
              :text="item.dashboard_title"
              dropdown-item
              v-if="_.isArray(dashboards)"
            >
            </ItpNavItem>
          </template>
        </ItpDropdownSubmenu>
      </ItpAppsDropdown>
    </BNavbarNav>
  </ItpUsercontrol>
</template>
